'use client';

import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { getBrowserSupabaseClient, withAuthTimeout } from '@west-tennessee-consulting/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface SharedAuthContextType {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  signInWithGoogle: (redirectPath?: string) => Promise<{ error?: string }>;
  signInWithMagicLink: (email: string) => Promise<{ error?: string }>;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const SharedAuthContext = createContext<SharedAuthContextType | undefined>(undefined);

export function useSharedAuth() {
  const context = useContext(SharedAuthContext);
  if (context === undefined) {
    throw new Error('useSharedAuth must be used within a SharedAuthProvider');
  }
  return context;
}

interface SharedAuthProviderProps {
  children: ReactNode;
}

export function SharedAuthProvider({ children }: SharedAuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // In a browser `@supabase/ssr` returns a module-level singleton, so this was
  // already stable there. It is NOT on the server pass of a client component,
  // where `isBrowser()` is false and every call builds a client — so memoize,
  // and the effect's dependency is then obviously stable on both passes.
  const supabase = useMemo(() => getBrowserSupabaseClient(), []);

  useEffect(() => {
    let cancelled = false;

    // Every exit path clears isLoading. The whole app renders behind this flag,
    // so a rejected — or, before withAuthTimeout, a never-settling — getSession
    // left the user on a permanent loading screen with nothing logged: an
    // unsettled promise raises no unhandledrejection for a global handler to
    // report. auth-js sets no fetch timeout, hence the bound.
    const getInitialSession = async () => {
      try {
        const { data } = await withAuthTimeout('getSession', () =>
          supabase.auth.getSession()
        );
        if (cancelled) return;
        const initialSession = data?.session ?? null;
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
      } catch (error) {
        if (cancelled) return;
        // Treat an unreadable session as signed out — the sign-in surface is
        // reachable, a spinner is not.
        // Deliberately not logged here: this package ships to sold products
        // and has no reporter seam (raw console.* is gated). The signed-out
        // render is the observable, actionable outcome.
        void error;
        setSession(null);
        setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: string, session: Session | null) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [supabase]);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error: error.message };
      }

      return {};
    } catch (error) {
      return { error: 'An unexpected error occurred' };
    }
  };

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName || '',
            last_name: lastName || '',
          },
        },
      });

      if (error) {
        return { error: error.message };
      }

      return {};
    } catch (error) {
      return { error: 'An unexpected error occurred' };
    }
  };

  const signOut = async () => {
    // Device scope only — never global (see SignOutScope in @west-tennessee-consulting/supabase).
    await supabase.auth.signOut({ scope: "local" });
  };

  const signInWithGoogle = async (redirectPath: string = '/dashboard') => {
    try {
      // Clear existing cookies before Google sign-in
      const cookies = document.cookie.split(";");
      cookies.forEach((cookie) => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        if (name && name.toLowerCase().includes('sb-')) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        }
      });

      const redirectUrl = `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectPath)}`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        return { error: error.message };
      }

      return {};
    } catch (error) {
      return { error: 'An unexpected error occurred' };
    }
  };

  const signInWithMagicLink = async (email: string) => {
    try {
      // Clear existing cookies before magic link
      const cookies = document.cookie.split(";");
      cookies.forEach((cookie) => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        if (name && name.toLowerCase().includes('sb-')) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        }
      });

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        return { error: error.message };
      }

      return {};
    } catch (error) {
      return { error: 'An unexpected error occurred' };
    }
  };

  const value: SharedAuthContextType = {
    user,
    session,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithMagicLink,
    isLoading,
    isAuthenticated: !!user,
  };

  return <SharedAuthContext.Provider value={value}>{children}</SharedAuthContext.Provider>;
}
